// Roche Research analytics tracker - writes to Supabase
import { createClient } from '@supabase/supabase-js';

const SB_URL = 'https://tzuejgikgvpazztdhojh.supabase.co';
const SB_KEY = 'sb_publishable_eWpZ7kWyxmS7sC_3ADFr6Q_UQfgYF7Q';
const sb = createClient(SB_URL, SB_KEY);

// Get or create persistent session ID (survives reload)
function getSessionId() {
  let sid = sessionStorage.getItem('rr_sid');
  if (!sid) {
    sid = 's_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem('rr_sid', sid);
  }
  return sid;
}

function isReturning() {
  const visited = localStorage.getItem('rr_visited');
  if (!visited) {
    localStorage.setItem('rr_visited', '1');
    return false;
  }
  return true;
}

function getDevice() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'Mac';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Other';
}

let sessionStarted = false;

async function ensureSession() {
  if (sessionStarted) return;
  sessionStarted = true;
  const sid = getSessionId();
  try {
    await sb.from('analytics_sessions').upsert({
      session_id: sid,
      last_seen: new Date().toISOString(),
      is_returning: isReturning(),
      referrer: document.referrer || null,
      device_type: getDevice(),
    }, { onConflict: 'session_id' });
  } catch (e) {
    console.warn('session start failed', e);
  }
}

// Touch session (update last_seen + page_count) - throttled to once every 10sec
let lastTouch = 0;
async function touchSession() {
  const now = Date.now();
  if (now - lastTouch < 10000) return;
  lastTouch = now;
  try {
    await sb.rpc('increment_session_pages', { sid: getSessionId() }).then(() => {}, () => {});
    await sb.from('analytics_sessions').update({
      last_seen: new Date().toISOString(),
    }).eq('session_id', getSessionId());
  } catch (e) {}
}

export async function track(eventType, opts = {}) {
  try {
    await ensureSession();
    const sid = getSessionId();
    await sb.from('analytics_events').insert({
      session_id: sid,
      event_type: eventType,
      page: opts.page || window.location.pathname,
      product_code: opts.productCode || null,
      product_name: opts.productName || null,
      metadata: opts.metadata || null,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent.slice(0, 200),
      device_type: getDevice(),
      duration_ms: opts.durationMs || null,
    });
    touchSession();
  } catch (e) {
    console.warn('track failed', eventType, e);
  }
}

// Auto-track page views with time-on-page
let pageStart = Date.now();
let currentPage = null;

export function trackPage(pageName) {
  // If we're switching pages, log the previous one's duration
  if (currentPage && currentPage !== pageName) {
    const duration = Date.now() - pageStart;
    track('page_exit', { page: currentPage, durationMs: duration });
  }
  currentPage = pageName;
  pageStart = Date.now();
  track('page_view', { page: pageName });
}

// Track time on page when user leaves the site
window.addEventListener('beforeunload', () => {
  if (currentPage) {
    const duration = Date.now() - pageStart;
    // Use sendBeacon for unload reliability
    try {
      navigator.sendBeacon(
        `${SB_URL}/rest/v1/analytics_events`,
        new Blob([JSON.stringify({
          session_id: getSessionId(),
          event_type: 'page_exit',
          page: currentPage,
          duration_ms: duration,
        })], { type: 'application/json' })
      );
    } catch (e) {}
  }
});

// Mark session as converted (call this after successful order)
export async function markConversion(orderNum) {
  try {
    await sb.from('analytics_sessions').update({
      converted: true,
      converted_order_id: orderNum,
    }).eq('session_id', getSessionId());
    await track('purchase', { metadata: { orderNum } });
  } catch (e) {
    console.warn('conversion mark failed', e);
  }
}

